<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LeadScoreBadge from 'dashboard/components-next/pipeline/LeadScoreBadge.vue';

const props = defineProps({
  pipelineCard: {
    type: Object,
    required: true,
  },
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'updated']);

const store = useStore();
const { t } = useI18n();

const newScore = ref(props.pipelineCard.lead_score || 0);
const isSubmitting = ref(false);
const submitError = ref('');
const minimumScore = 0;
const maximumScore = 100;

watch(
  () => props.show,
  val => {
    if (val) {
      newScore.value = props.pipelineCard.lead_score || 0;
      submitError.value = '';
    }
  }
);

const newCategory = computed(() => {
  if (newScore.value >= 70) return 'hot';
  if (newScore.value >= 40) return 'warm';
  return 'cold';
});

async function submitOverride() {
  if (isSubmitting.value) return;

  isSubmitting.value = true;
  submitError.value = '';
  try {
    await store.dispatch('pipelineCards/overrideLeadScore', {
      itemId: props.pipelineCard.id,
      score: newScore.value,
    });

    emit('updated');
    emit('close');
  } catch {
    submitError.value = t('PIPELINE.LEAD_SCORE.OVERRIDE_ERROR');
  } finally {
    isSubmitting.value = false;
  }
}

function handleClose() {
  if (!isSubmitting.value) {
    emit('close');
  }
}
</script>

<template>
  <Modal
    :show="show"
    :close-on-backdrop-click="!isSubmitting"
    :close-on-escape="!isSubmitting"
    :show-close-button="!isSubmitting"
    initial-focus-selector="#lead-score-override"
    @close="handleClose"
  >
    <section
      class="max-w-md p-4 sm:p-6"
      :aria-busy="isSubmitting"
      data-modal="lead-score-override"
    >
      <h2 class="mb-5 pr-10 text-heading-2 text-n-slate-12">
        {{ t('PIPELINE.LEAD_SCORE.OVERRIDE_TITLE') }}
      </h2>

      <div class="space-y-6">
        <!-- Current Score -->
        <div>
          <p class="mb-2 text-label text-n-slate-12">
            {{ t('PIPELINE.LEAD_SCORE.CURRENT_SCORE') }}
          </p>
          <LeadScoreBadge
            :score="pipelineCard.lead_score || 0"
            :factors="pipelineCard.lead_score_factors"
            show-label
          />
        </div>

        <!-- Slider -->
        <div>
          <label
            for="lead-score-override"
            class="mb-2 block text-label text-n-slate-12"
          >
            {{ t('PIPELINE.LEAD_SCORE.NEW_SCORE') }}
          </label>
          <input
            id="lead-score-override"
            v-model.number="newScore"
            type="range"
            name="lead-score"
            :min="minimumScore"
            :max="maximumScore"
            step="5"
            :disabled="isSubmitting"
            :aria-valuenow="newScore"
            class="!mb-0 h-12 w-full cursor-pointer appearance-none rounded-lg bg-transparent accent-n-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:cursor-not-allowed disabled:opacity-50 [&::-moz-range-track]:h-2 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:bg-n-alpha-2 [&::-webkit-slider-runnable-track]:h-2 [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-runnable-track]:bg-n-alpha-2"
          />
          <div class="flex items-center justify-between mt-2">
            <span class="text-label-small text-n-slate-11">
              {{ minimumScore }}
            </span>
            <div class="text-center">
              <div class="text-3xl font-bold text-n-slate-12">
                {{ newScore }}
              </div>
              <div class="text-label-small text-n-slate-11 uppercase">
                {{ t(`PIPELINE.LEAD_SCORE.${newCategory.toUpperCase()}_LEAD`) }}
              </div>
            </div>
            <span class="text-label-small text-n-slate-11">
              {{ maximumScore }}
            </span>
          </div>
        </div>
      </div>

      <p
        v-if="submitError"
        class="mt-5 rounded-lg bg-n-ruby-3 p-3 text-sm text-n-ruby-11"
        role="alert"
      >
        {{ submitError }}
      </p>

      <!-- Actions -->
      <footer
        class="mt-6 flex flex-col-reverse gap-2 border-t border-n-weak pt-4 sm:flex-row sm:justify-end"
      >
        <Button
          variant="ghost"
          color="slate"
          size="lg"
          :label="t('PIPELINE.CANCEL')"
          :disabled="isSubmitting"
          class="w-full sm:w-auto"
          @click="handleClose"
        />
        <Button
          size="lg"
          :label="t('PIPELINE.SAVE')"
          :is-loading="isSubmitting"
          :disabled="isSubmitting"
          class="w-full sm:w-auto"
          @click="submitOverride"
        />
      </footer>
    </section>
  </Modal>
</template>
